import {Alert, Button, Descriptions, Empty, Modal, Spin, Table, Tabs, Tooltip, Typography} from 'antd';
import {ExclamationCircleOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {useState} from 'react';
import {formatTimestamp} from "../../helpers/time";
import {formatFileSize, getId} from "../../helpers/file";
import i18n from '../../i18n';

interface StorageStats {
  totalSize: number;
  maxStorageSize: number | null;
  remainingSize: number | null;
  usagePercent: number | null;
  overQuota: boolean;
}

interface DailyTrafficMetric {
  date: string;
  trafficBytes: number;
  requestCount: number;
}

interface UserTrafficStats {
  days: number;
  retentionDays: number;
  enabled: boolean;
  totalTrafficBytes: number;
  totalRequestCount: number;
  todayTrafficBytes: number;
  todayRequestCount: number;
  peakDailyTrafficBytes: number;
  peakDailyDate: string;
  dailyStats: DailyTrafficMetric[];
  timestamp: number;
}

interface TrafficFetchResult {
  data: UserTrafficStats | null;
  failed: boolean;
}

interface HourlyUploadMetric {
  hour: number;
  count: number;
}

interface UserUploadUsage {
  date: string;
  timezone: string;
  statisticsAvailable: boolean;
  totalCount: number | null;
  limitApplicable: boolean;
  dailyLimit: number | null;
  unlimited: boolean;
  hourly: HourlyUploadMetric[];
  timestamp: number;
}

interface UploadSampleRecord {
  fileName: string;
  clientIp: string;
  uploadedAt: number | string;
}

interface UploadSamples {
  date: string;
  timezone: string;
  hour: number;
  uploadCount: number | string | null;
  sampleLimit: number;
  statisticsAvailable: boolean;
  records: UploadSampleRecord[];
  timestamp: number | string;
}

interface UploadUsageFetchResult {
  data: UserUploadUsage | null;
  failed: boolean;
}

interface RecentAccessRecord {
  fileName: string;
  accessTime: number | string;
  clientIp: string;
}

interface RecentAccessResponse {
  records: RecentAccessRecord[];
  timestamp: number | string;
}

interface RecentAccessTableRecord extends RecentAccessRecord {
  rowKey: string;
}

interface RecentAccessFetchResult {
  data: RecentAccessResponse | null;
  failed: boolean;
}

const {Title} = Typography;

const HourlyUploadTable = ({usage}: {usage: UserUploadUsage}) => {
  const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, options);
  const [sampleHour, setSampleHour] = useState<number | null>(null);
  const [sampleData, setSampleData] = useState<UploadSamples | null>(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleFailed, setSampleFailed] = useState(false);

  const hourRange = (hour: number) => {
    const start = String(hour).padStart(2, '0');
    const end = String(hour + 1).padStart(2, '0');
    return `${start}:00–${end}:00`;
  };

  const loadSamples = async (hour: number) => {
    setSampleHour(hour);
    setSampleData(null);
    setSampleFailed(false);
    setSampleLoading(true);
    try {
      const response = await fetch(`/img/upload-usage/samples?hour=${hour}`);
      if (!response.ok) {
        throw new Error(`Upload sample request failed with status ${response.status}`);
      }
      setSampleData(await response.json() as UploadSamples);
    } catch (error) {
      console.error('Failed to fetch upload samples:', error);
      setSampleFailed(true);
    } finally {
      setSampleLoading(false);
    }
  };

  return (
    <>
      <Table<HourlyUploadMetric>
        className="usage-hourly-table"
        rowKey="hour"
        size="small"
        pagination={false}
        scroll={{x: 360, y: 320}}
        dataSource={usage.hourly}
        columns={[
          {
            title: t('usageInfo.hourRange'),
            dataIndex: 'hour',
            key: 'hour',
            width: 180,
            render: (hour: number) => hourRange(hour),
          },
          {
            title: t('usageInfo.hourlyUploadCount'),
            dataIndex: 'count',
            key: 'count',
            align: 'right',
            render: (count: number, metric: HourlyUploadMetric) => (
              <span>
                {t('usageInfo.uploadCountValue', {count: (count || 0).toLocaleString()})}
                {count > 0 && (
                  <Tooltip title={t('usageInfo.viewUploadSamples')}>
                    <Button
                      type="link"
                      size="small"
                      aria-label={t('usageInfo.viewUploadSamples')}
                      icon={<QuestionCircleOutlined />}
                      onClick={() => loadSamples(metric.hour)}
                    />
                  </Tooltip>
                )}
              </span>
            ),
          },
        ]}
      />
      <Modal
        open={sampleHour != null}
        title={sampleHour == null ? t('usageInfo.uploadSamples') : `${t('usageInfo.uploadSamples')} · ${hourRange(sampleHour)}`}
        footer={null}
        width={720}
        onCancel={() => setSampleHour(null)}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          message={t('usageInfo.uploadSampleNotice')}
          style={{marginBottom: 16}}
        />
        {sampleLoading ? (
          <div style={{textAlign: 'center', padding: '32px 0'}}><Spin /></div>
        ) : sampleFailed ? (
          <Alert type="error" showIcon message={t('usageInfo.uploadSamplesFetchFailed')} />
        ) : sampleData?.statisticsAvailable === false ? (
          <Alert type="error" showIcon message={t('usageInfo.uploadSamplesUnavailable')} />
        ) : sampleData && sampleData.records.length > 0 ? (
          <Table<UploadSampleRecord>
            size="small"
            rowKey={(record, index) => `${record.uploadedAt}-${index}`}
            dataSource={sampleData.records}
            pagination={{pageSize: 10, showSizeChanger: false}}
            scroll={{x: 560, y: 420}}
            columns={[
              {
                title: t('usageInfo.fileName'),
                dataIndex: 'fileName',
                key: 'fileName',
                ellipsis: {showTitle: true},
              },
              {
                title: t('usageInfo.uploadIp'),
                dataIndex: 'clientIp',
                key: 'clientIp',
                width: 150,
              },
              {
                title: t('usageInfo.uploadTime'),
                dataIndex: 'uploadedAt',
                key: 'uploadedAt',
                width: 180,
                render: (value: number | string) => formatTimestamp(String(value)),
              },
            ]}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('usageInfo.noUploadSamples')} />
        )}
      </Modal>
    </>
  );
};

export const showUsageInfo = () => {
  Modal.destroyAll();
  const uid = getId();
  const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, options);
  
  // 获取在线情况
  const fetchOnlineInfo = fetch('/img/onlineInfo').then(res => res.json());
  
  // 获取存储统计
  const fetchStorageStats = fetch('/img/stat').then(res => res.json() as Promise<StorageStats>);

  // 上传统计独立降级，Redis 波动不能影响其他使用情况。
  const fetchUploadUsage: Promise<UploadUsageFetchResult> = fetch('/img/upload-usage')
    .then(async res => {
      if (!res.ok) {
        throw new Error(`Upload usage request failed with status ${res.status}`);
      }
      return {
        data: await res.json() as UserUploadUsage,
        failed: false,
      };
    })
    .catch(error => {
      console.error('Failed to fetch upload usage:', error);
      return {data: null, failed: true};
    });

  // 流量统计独立降级，避免其请求失败时阻断原有使用情况。
  const fetchTrafficStats: Promise<TrafficFetchResult> = fetch('/img/traffic-stat?days=30')
    .then(async res => {
      if (!res.ok) {
        throw new Error(`Traffic request failed with status ${res.status}`);
      }
      return {
        data: await res.json() as UserTrafficStats,
        failed: false,
      };
    })
    .catch(error => {
      console.error('Failed to fetch traffic stats:', error);
      return {data: null, failed: true};
    });

  // 最近访问独立降级，避免其请求失败时阻断其他使用情况。
  const fetchRecentAccesses: Promise<RecentAccessFetchResult> = fetch('/img/recent-accesses')
    .then(async res => {
      if (!res.ok) {
        throw new Error(`Recent access request failed with status ${res.status}`);
      }
      return {
        data: await res.json() as RecentAccessResponse,
        failed: false,
      };
    })
    .catch(error => {
      console.error('Failed to fetch recent accesses:', error);
      return {data: null, failed: true};
    });

  const handleClearAll = (totalSize: number) => {
    Modal.confirm({
      title: t('usageInfo.clearAllConfirm'),
      icon: <ExclamationCircleOutlined style={{color: '#cf1322'}} />,
      content: (
        <div>
          <p>{t('usageInfo.totalSize')}: <strong>{formatFileSize(totalSize)}</strong></p>
          <p style={{color: '#cf1322', marginTop: 8}}>⚠️ 此操作将永久删除所有文件，无法恢复！</p>
        </div>
      ),
      okText: t('usageInfo.clearAll'),
      okButtonProps: {danger: true},
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const res = await fetch('/img/clear-all', {
            method: 'POST',
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
            body: new URLSearchParams({uid})
          });
          const data = await res.json();
          if (data.success) {
            Modal.success({
              title: t('common.success'),
              content: t('usageInfo.clearAllSuccess', {count: data.data?.deletedCount || 0}),
              onOk: () => {
                showUsageInfo();
              }
            });
          } else {
            Modal.error({title: t('common.failed'), content: data.message || t('usageInfo.clearAllFailed')});
          }
        } catch (error) {
          console.error('Clear all failed:', error);
          Modal.error({title: t('common.error'), content: t('usageInfo.clearAllFailed')});
        }
      }
    });
  };

  return Promise.all([fetchOnlineInfo, fetchStorageStats, fetchUploadUsage, fetchTrafficStats, fetchRecentAccesses])
    .then(([onlineData, storageData, uploadUsageResult, trafficResult, recentAccessResult]) => {
      const trafficData = trafficResult.data;
      const uploadUsage = uploadUsageResult.data;
      const dailyTraffic = [...(trafficData?.dailyStats || [])].reverse();
      const hasDailyTraffic = dailyTraffic.some(item => item.trafficBytes > 0 || item.requestCount > 0);
      const recentAccesses: RecentAccessTableRecord[] = (recentAccessResult.data?.records || [])
        .map((record, index) => ({
          ...record,
          rowKey: `${record.accessTime}-${record.fileName}-${record.clientIp}-${index}`,
        }));
      const items = [
        {
          key: '1',
          label: t('usageInfo.onlineStatus'),
          children: (
            <>
              {
                Object.entries(onlineData)
                  .map(([key, value]) => 
                    <p key={key}>
                      {key === uid ? t('usageInfo.currentDevice') : key}: <b>{formatTimestamp(value as string)}</b>
                    </p>
                  )
              }
            </>
          ),
        },
        {
          key: '2',
          label: t('usageInfo.storageStats'),
          children: (
            <div>
              {storageData ? (
                <>
                  <p>{t('usageInfo.totalSize')}: <b>{formatFileSize(storageData.totalSize)}</b></p>
                  <p>{t('usageInfo.maxStorageSize')}: <b>
                    {storageData.maxStorageSize == null
                      ? t('usageInfo.unlimited')
                      : formatFileSize(storageData.maxStorageSize)}
                  </b></p>
                  {storageData.maxStorageSize != null && (
                    <>
                      <p>{t('usageInfo.remainingSize')}: <b>
                        {formatFileSize(storageData.remainingSize || 0)}
                      </b></p>
                      <p>{t('usageInfo.usagePercent')}: <b>{storageData.usagePercent || 0}%</b></p>
                      <p>{t('usageInfo.quotaStatus')}: <b style={{ color: storageData.overQuota ? '#cf1322' : undefined }}>
                        {storageData.overQuota ? t('usageInfo.overQuota') : t('usageInfo.withinQuota')}
                      </b></p>
                    </>
                  )}
                  {storageData.maxStorageSize != null && storageData.totalSize > 0 && (
                    <div style={{marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0'}}>
                      <Button
                        type="primary"
                        danger
                        size="small"
                        onClick={() => handleClearAll(storageData.totalSize)}
                      >
                        {t('usageInfo.clearAll')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p>{t('usageInfo.noStorageStats')}</p>
              )}
            </div>
          ),
        },
        {
          key: 'upload',
          label: t('usageInfo.uploadStats'),
          children: (
            <div>
              {uploadUsageResult.failed || !uploadUsage ? (
                <Alert type="error" showIcon message={t('usageInfo.uploadStatsFetchFailed')} />
              ) : (
                <>
                  <Descriptions size="small" bordered column={{xs: 1, sm: 2}}>
                    <Descriptions.Item label={t('usageInfo.todayUploadCount')}>
                      <b>
                        {uploadUsage.statisticsAvailable && uploadUsage.totalCount != null
                          ? t('usageInfo.uploadCountValue', {count: uploadUsage.totalCount.toLocaleString()})
                          : '-'}
                      </b>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('usageInfo.dailyUploadLimit')}>
                      <b>
                        {!uploadUsage.limitApplicable
                          ? t('usageInfo.dailyUploadNotApplicable')
                          : uploadUsage.unlimited
                            ? t('usageInfo.dailyUploadUnlimited')
                            : uploadUsage.dailyLimit == null
                              ? '-'
                              : t('usageInfo.dailyUploadLimitValue', {
                                count: uploadUsage.dailyLimit.toLocaleString(),
                              })}
                      </b>
                    </Descriptions.Item>
                  </Descriptions>
                  <Alert
                    type="info"
                    showIcon
                    message={t('usageInfo.uploadStatsDelayNotice')}
                    style={{marginTop: 16}}
                  />
                  {!uploadUsage.statisticsAvailable ? (
                    <Alert
                      type="error"
                      showIcon
                      message={t('usageInfo.uploadStatsUnavailable')}
                      style={{marginTop: 16}}
                    />
                  ) : (
                    <>
                      <Title level={5} style={{marginTop: 20, marginBottom: 12}}>
                        {t('usageInfo.hourlyUploadDetails')}
                      </Title>
                      <HourlyUploadTable usage={uploadUsage} />
                    </>
                  )}
                </>
              )}
            </div>
          ),
        },
        {
          key: '3',
          label: t('usageInfo.trafficStats'),
          children: (
            <div>
              <Alert
                type="warning"
                showIcon
                message={t('usageInfo.trafficDisclaimer')}
                style={{marginBottom: 16}}
              />
              {trafficResult.failed ? (
                <Alert type="error" showIcon message={t('usageInfo.trafficFetchFailed')} />
              ) : trafficData ? (
                <>
                  {!trafficData.enabled && (
                    <Alert
                      type="info"
                      showIcon
                      message={t('usageInfo.trafficStatsPaused')}
                      style={{marginBottom: 16}}
                    />
                  )}
                  <Descriptions size="small" bordered column={{xs: 1, sm: 2}}>
                    <Descriptions.Item label={t('usageInfo.todayTraffic')}>
                      <b>{formatFileSize(trafficData.todayTrafficBytes || 0)}</b>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('usageInfo.todayRequests')}>
                      <b>{(trafficData.todayRequestCount || 0).toLocaleString()}</b>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('usageInfo.recentTraffic')}>
                      <b>{formatFileSize(trafficData.totalTrafficBytes || 0)}</b>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('usageInfo.recentRequests')}>
                      <b>{(trafficData.totalRequestCount || 0).toLocaleString()}</b>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('usageInfo.peakDailyTraffic')}>
                      <b>{formatFileSize(trafficData.peakDailyTrafficBytes || 0)}</b>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('usageInfo.peakDate')}>
                      <b>{trafficData.totalTrafficBytes > 0 ? trafficData.peakDailyDate : '-'}</b>
                    </Descriptions.Item>
                  </Descriptions>
                  <Title level={5} style={{marginTop: 20, marginBottom: 12}}>
                    {t('usageInfo.dailyTrafficDetails')}
                  </Title>
                  {hasDailyTraffic ? (
                    <Table<DailyTrafficMetric>
                      rowKey="date"
                      size="small"
                      pagination={false}
                      scroll={{x: 440, y: 280}}
                      dataSource={dailyTraffic}
                      columns={[
                        {
                          title: t('usageInfo.date'),
                          dataIndex: 'date',
                          key: 'date',
                          width: 130,
                        },
                        {
                          title: t('usageInfo.traffic'),
                          dataIndex: 'trafficBytes',
                          key: 'trafficBytes',
                          render: (value: number) => formatFileSize(value || 0),
                        },
                        {
                          title: t('usageInfo.requests'),
                          dataIndex: 'requestCount',
                          key: 'requestCount',
                          render: (value: number) => (value || 0).toLocaleString(),
                        },
                      ]}
                    />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('usageInfo.noTrafficStats')} />
                  )}
                </>
              ) : null}
            </div>
          ),
        },
        {
          key: '4',
          label: t('usageInfo.recentAccesses'),
          children: (
            <div>
              {recentAccessResult.failed ? (
                <Alert type="error" showIcon message={t('usageInfo.recentAccessFetchFailed')} />
              ) : recentAccesses.length > 0 ? (
                <Table<RecentAccessTableRecord>
                  rowKey="rowKey"
                  size="small"
                  pagination={false}
                  scroll={{x: 620, y: 320}}
                  dataSource={recentAccesses}
                  columns={[
                    {
                      title: t('usageInfo.fileName'),
                      dataIndex: 'fileName',
                      key: 'fileName',
                      width: 270,
                      render: (value: string) => (
                        <Tooltip title={value}>
                          <Typography.Text ellipsis style={{maxWidth: 240, display: 'block'}}>
                            {value}
                          </Typography.Text>
                        </Tooltip>
                      ),
                    },
                    {
                      title: t('usageInfo.accessTime'),
                      dataIndex: 'accessTime',
                      key: 'accessTime',
                      width: 190,
                      render: (value: number | string) => formatTimestamp(String(value)),
                    },
                    {
                      title: t('usageInfo.accessIp'),
                      dataIndex: 'clientIp',
                      key: 'clientIp',
                      width: 160,
                    },
                  ]}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('usageInfo.noRecentAccesses')} />
              )}
            </div>
          ),
        },
      ];

      Modal.info({
        title: t('usageInfo.title'),
        width: 720,
        content: (
          <Tabs defaultActiveKey="1" items={items} />
        ),
      });
    })
    .catch(error => {
      console.error('Failed to fetch usage info:', error);
      Modal.error({
        title: t('usageInfo.error'),
        content: t('usageInfo.fetchFailed'),
      });
    });
};
